// ─── Collage Element Presets ──────────────────────────────────
// Pre-styled element templates users can pick from when adding
// to a collage section. Each preset is a complete element config
// minus the ID (generated on insert).

export const SYSTEM_PRESETS = [
  // ━━━ Images ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    category: 'Images',
    presets: [
      {
        name: 'Vintage Polaroid',
        description: 'Classic polaroid frame with caption',
        type: 'image_card',
        style: 'polaroid',
        src: '',
        caption: 'a moment to remember',
        rotation: -2,
        intent: { role: 'image', importance: 'high', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidText', animationPriority: 2 },
        layout: { desktop: { x: 0.5, y: 0.4, w: 0.34, z: 5 }, mobile: { x: 0.5, y: 0.35, w: 0.68, z: 5 } },
      },
      {
        name: 'Clean Photo',
        description: 'Borderless photo with soft shadow',
        type: 'image_card',
        style: 'plain-photo',
        src: '',
        rotation: 0,
        intent: { role: 'image', importance: 'high', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidText', animationPriority: 2 },
        layout: { desktop: { x: 0.35, y: 0.3, w: 0.28, z: 3 }, mobile: { x: 0.4, y: 0.25, w: 0.58, z: 3 } },
      },
      {
        name: 'Feature Photo',
        description: 'Large hero-sized photo',
        type: 'image_card',
        style: 'plain-photo-large',
        src: '',
        rotation: 0,
        intent: { role: 'image', importance: 'high', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidText', animationPriority: 2 },
        layout: { desktop: { x: 0.6, y: 0.45, w: 0.38, z: 3 }, mobile: { x: 0.55, y: 0.4, w: 0.7, z: 3 } },
      },
      {
        name: 'Tilted Polaroid',
        description: 'Playful angled polaroid',
        type: 'image_card',
        style: 'polaroid',
        src: '',
        caption: '',
        rotation: 4,
        intent: { role: 'image', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidText', animationPriority: 3 },
        layout: { desktop: { x: 0.7, y: 0.55, w: 0.26, z: 6 }, mobile: { x: 0.65, y: 0.5, w: 0.55, z: 6 } },
      },
    ],
  },
  // ━━━ Notes ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    category: 'Notes',
    presets: [
      {
        name: 'Handwritten Note',
        description: 'Paper note with handwriting font',
        type: 'text_note',
        style: 'handwritten-paper',
        content: 'Write something personal here...',
        rotation: -3,
        intent: { role: 'decorative', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'allow', animationPriority: 4 },
        layout: { desktop: { x: 0.3, y: 0.6, w: 0.22, z: 6 }, mobile: { x: 0.35, y: 0.55, w: 0.44, z: 6 } },
      },
      {
        name: 'Torn Paper',
        description: 'Torn-edge paper scrap with organic shape',
        type: 'text_note',
        style: 'torn-note',
        content: 'love is patient\nlove is kind\nlove never fails',
        rotation: -5,
        intent: { role: 'decorative', importance: 'low', behavior: 'decorative', mobileBehavior: 'preserve', overlapPolicy: 'allow', animationPriority: 5 },
        layout: { desktop: { x: 0.25, y: 0.75, w: 0.24, z: 7 }, mobile: { x: 0.3, y: 0.7, w: 0.52, z: 7 } },
      },
      {
        name: 'Love Note',
        description: 'Gentle tilted note for personal words',
        type: 'text_note',
        style: 'handwritten-paper',
        content: 'to the love of my life,\nthank you for everything.',
        rotation: 2,
        intent: { role: 'decorative', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'allow', animationPriority: 4 },
        layout: { desktop: { x: 0.72, y: 0.65, w: 0.2, z: 6 }, mobile: { x: 0.68, y: 0.6, w: 0.4, z: 6 } },
      },
    ],
  },
  // ━━━ Text ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    category: 'Text',
    presets: [
      {
        name: 'Typewriter Paragraph',
        description: 'Monospaced text block for stories',
        type: 'text_block',
        style: 'typewriter',
        content: 'Your story begins here.\n\nWrite about how you met, your journey together, or what this day means to you.',
        intent: { role: 'bodyText', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidImportant', animationPriority: 3 },
        layout: { desktop: { x: 0.3, y: 0.45, w: 0.3, z: 4 }, mobile: { x: 0.35, y: 0.4, w: 0.65, z: 4 } },
      },
      {
        name: 'Right-Aligned Caption',
        description: 'Small right-aligned typewriter text',
        type: 'text_block',
        style: 'typewriter-small-right',
        content: 'Thank you for being part of our story.\n\nWith love',
        intent: { role: 'bodyText', importance: 'medium', behavior: 'flexible', mobileBehavior: 'preserve', overlapPolicy: 'avoidImportant', animationPriority: 3 },
        layout: { desktop: { x: 0.75, y: 0.2, w: 0.22, z: 4 }, mobile: { x: 0.7, y: 0.25, w: 0.48, z: 4 } },
      },
      {
        name: 'Editorial Quote',
        description: 'Large serif text for poetic quotes',
        type: 'text_block',
        style: 'editorial-right',
        content: 'Two souls\nwith but\na single thought',
        intent: { role: 'decorative', importance: 'low', behavior: 'decorative', mobileBehavior: 'hide', overlapPolicy: 'allow', animationPriority: 5 },
        layout: { desktop: { x: 0.8, y: 0.8, w: 0.2, z: 4 }, mobile: { x: 0.75, y: 0.85, w: 0.36, z: 4 } },
      },
      {
        name: 'Micro Label',
        description: 'Tiny uppercase tracking label',
        type: 'text_block',
        style: 'micro-label-center',
        content: 'est. 2026',
        intent: { role: 'metaText', importance: 'low', behavior: 'decorative', mobileBehavior: 'hide', overlapPolicy: 'allow', animationPriority: 6 },
        layout: { desktop: { x: 0.5, y: 0.88, w: 0.2, z: 2 }, mobile: { x: 0.5, y: 0.82, w: 0.3, z: 2 } },
      },
    ],
  },
  // ━━━ Titles ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  {
    category: 'Titles',
    presets: [
      {
        name: 'Hero Title',
        description: 'Main headline with subtitle, date, and CTA',
        type: 'title_block',
        style: 'hero-title',
        title: 'Your Names',
        subtitle: 'Join us for a celebration of love.',
        meta: 'Date \u00b7 Location',
        buttonText: 'See more',
        buttonHref: '#story-collage',
        intent: { role: 'primaryHeadline', importance: 'high', behavior: 'fixed', mobileBehavior: 'preserve', overlapPolicy: 'none', animationPriority: 1 },
        layout: { desktop: { x: 0.5, y: 0.92, w: 0.6, z: 4 }, mobile: { x: 0.5, y: 0.88, w: 0.84, z: 4 } },
      },
      {
        name: 'Closing Message',
        description: 'Footer-style title for ending sections',
        type: 'title_block',
        style: 'footer-title',
        title: "We can't wait to see you.",
        subtitle: 'Your Names',
        intent: { role: 'primaryHeadline', importance: 'high', behavior: 'fixed', mobileBehavior: 'preserve', overlapPolicy: 'none', animationPriority: 1 },
        layout: { desktop: { x: 0.5, y: 0.5, w: 0.6, z: 4 }, mobile: { x: 0.5, y: 0.5, w: 0.84, z: 4 } },
      },
    ],
  },
];
